export type Platform = 'linkedin' | 'twitter' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'threads';

export type PostStatus = 'scheduled' | 'unscheduled' | 'approved' | 'pending_approval' | 'changes_requested';

export type MediaType = 'image' | 'video' | 'carousel' | 'text';

export type MediaSource = 'url' | 'google_drive' | 'r2';

export interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  source: MediaSource;
  originalUrl?: string; // Original Google Drive link for reference
  thumbnailUrl?: string;
  objectKey?: string; // R2 object key for deletion/cleanup
  uploadedAt?: Date; // When the file was uploaded (for cleanup tracking)
  fileSize?: number; // File size in bytes (for quota tracking)
}

export interface PostCaption {
  platform: Platform;
  text: string;
}

export interface PlatformMedia {
  platform: Platform;
  mediaItems: MediaItem[];
}

export const PLATFORM_CHAR_LIMITS: Record<Platform, number> = {
  linkedin: 3000,
  twitter: 280,
  instagram: 2200,
  facebook: 63206,
  tiktok: 2200,
  youtube: 5000,
  threads: 500,
};

export interface Post {
  id: string;
  title: string;
  source: string;
  platforms: Platform[];
  captions: PostCaption[];
  mediaType: MediaType;
  mediaItems: MediaItem[]; // Default/shared media for all platforms
  platformMedia?: PlatformMedia[]; // Optional per-platform media overrides
  thumbnailUrl?: string;
  status: PostStatus;
  scheduledDate?: Date;
  scheduledTime?: string;
  approvalToken?: string; // Current active approval token
  approvedAt?: Date;
  approvedBy?: string;
  comments?: Comment[];
  mediaCleaned?: boolean; // True if media was auto-deleted after expiry
  mediaCleanedAt?: Date; // When media was cleaned up
  clientId?: string; // Client this post belongs to (null = personal)
}

export interface Comment {
  id: string;
  text: string;
  author: string;
  createdAt: Date;
  resolved: boolean;
  selectionStart?: number;
  selectionEnd?: number;
}

export type FilterType = 'all' | 'scheduled' | 'unscheduled';
export type ViewType = 'list' | 'calendar';

export interface BatchApprovalToken {
  token: string;
  postIds: string[];
  createdAt: number;
  expiresAt: number;
}

// Pricing Plans
export type PlanId = 'free' | 'pro';

export interface Plan {
  id: PlanId;
  name: string;
  price: number;
  yearlyPrice: number; // Annual price (15% discount)
  description: string;
  features: {
    clients: number;
    postsPerMonth: number;
    storageGB: number;
    branding: 'watermark' | 'none';
    prioritySupport: boolean;
  };
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    yearlyPrice: 0,
    description: 'Get started',
    features: {
      clients: 3,
      postsPerMonth: 30,
      storageGB: 0.5, // 500MB
      branding: 'watermark',
      prioritySupport: false,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 19,
    yearlyPrice: 194, // ~15% off ($16.17/mo)
    description: 'For professionals',
    features: {
      clients: 25,
      postsPerMonth: 2500,
      storageGB: 25,
      branding: 'none',
      prioritySupport: true,
    },
  },
};

export interface Subscription {
  planId: PlanId;
  status: 'active' | 'canceled' | 'past_due';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  postsUsedThisMonth: number;
  storageUsedBytes: number; // Track storage usage for R2
}

// Storage limits per plan (in bytes)
export const STORAGE_LIMITS: Record<PlanId, number> = {
  free: 500 * 1024 * 1024, // 500 MB
  pro: 25 * 1024 * 1024 * 1024, // 25 GB
};

// Upload limits
export const UPLOAD_LIMITS = {
  IMAGE_MAX_SIZE: 5 * 1024 * 1024, // 5 MB
  VIDEO_MAX_SIZE: 100 * 1024 * 1024, // 100 MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/quicktime', 'video/webm'],
};

// Asset Library types (Google Drive-style file management)
export interface Asset {
  id: string;
  userId: string;
  name: string;
  type: 'image' | 'video';
  mimeType: string;
  size: number; // bytes
  url: string;
  thumbnailUrl?: string;
  objectKey: string; // R2 object key
  folderId?: string; // null for root
  clientId?: string; // Client this asset belongs to (null = personal)
  createdAt: Date;
  updatedAt: Date;
}

export interface AssetFolder {
  id: string;
  userId: string;
  name: string;
  parentId?: string; // null for root
  clientId?: string; // Client this folder belongs to (null = personal)
  createdAt: Date;
  updatedAt: Date;
}

export type AssetViewType = 'grid' | 'list';
export type AssetSortBy = 'name' | 'date' | 'size' | 'type';

// Client Management types
export interface Client {
  id: string;
  managerId: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  brandColor?: string;
  notes?: string;
  postCount?: number;
  assetCount?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Client limits per plan
export const CLIENT_LIMITS: Record<PlanId, number> = {
  free: 3,
  pro: 25,
};
